<template>
  <div>
    <b-card>
      <!-- Header -->
      <b-row class="mb-3">
        <b-col>
          <h3 class="mb-0">User Management</h3>
          <p class="text-muted">Manage students and teachers</p>
        </b-col>
        <b-col class="text-right">
          <b-button variant="primary" @click="refreshUsers">
            <feather type="refresh-cw" class="feather-sm"></feather>
            Refresh
          </b-button>
        </b-col>
      </b-row>

      <!-- Filters -->
      <b-row class="mb-3">
        <b-col md="3">
          <b-form-select v-model="filter.userType" @change="filterUsers">
            <b-form-select-option :value="null">All Users</b-form-select-option>
            <b-form-select-option :value="0">Students Only</b-form-select-option>
            <b-form-select-option :value="1">Teachers Only</b-form-select-option>
          </b-form-select>
        </b-col>
        <b-col md="3">
          <b-form-select v-model="filter.status" @change="filterUsers">
            <b-form-select-option :value="null">All Status</b-form-select-option>
            <b-form-select-option value="active">Active</b-form-select-option>
            <b-form-select-option value="suspended">Suspended</b-form-select-option>
            <b-form-select-option value="verified">Verified</b-form-select-option>
            <b-form-select-option value="unverified">Unverified</b-form-select-option>
          </b-form-select>
        </b-col>
        <b-col md="6">
          <b-form-input
            v-model="filter.search"
            placeholder="Search by name, email, or phone"
            @input="searchUsers"
          ></b-form-input>
        </b-col>
      </b-row>

      <!-- Stats Cards -->
      <b-row class="mb-4">
        <b-col md="3">
          <b-card bg-variant="primary" text-variant="white">
            <h3 class="mb-0">{{ stats.totalUsers || 0 }}</h3>
            <p class="mb-0">Total Users</p>
          </b-card>
        </b-col>
        <b-col md="3">
          <b-card bg-variant="info" text-variant="white">
            <h3 class="mb-0">{{ stats.students || 0 }}</h3>
            <p class="mb-0">Students</p>
          </b-card>
        </b-col>
        <b-col md="3">
          <b-card bg-variant="success" text-variant="white">
            <h3 class="mb-0">{{ stats.teachers || 0 }}</h3>
            <p class="mb-0">Teachers</p>
          </b-card>
        </b-col>
        <b-col md="3">
          <b-card bg-variant="warning" text-variant="white">
            <h3 class="mb-0">{{ stats.pendingVerification || 0 }}</h3>
            <p class="mb-0">Pending Verification</p>
          </b-card>
        </b-col>
      </b-row>

      <!-- Users Table -->
      <b-table
        :items="filteredUsers"
        :fields="fields"
        :busy="loading"
        :per-page="perPage"
        :current-page="currentPage"
        striped
        hover
        responsive
        show-empty
      >
        <template #table-busy>
          <div class="text-center my-5">
            <b-spinner class="align-middle mr-2"></b-spinner>
            <strong>Loading users...</strong>
          </div>
        </template>

        <template #cell(name)="data">
          <div class="d-flex align-items-center">
            <b-avatar
              :src="data.item.profile?.profile_img"
              :text="displayName(data.item)[0]"
              variant="primary"
              class="mr-2"
            ></b-avatar>
            <div>
              <div class="font-weight-bold">{{ displayName(data.item) }}</div>
              <small class="text-muted">{{ data.item.email }}</small>
            </div>
          </div>
        </template>

        <template #cell(user_type)="data">
          <b-badge :variant="data.value === 1 ? 'success' : 'info'">
            {{ data.value === 1 ? 'Teacher' : 'Student' }}
          </b-badge>
        </template>

        <template #cell(phone)="data">
          {{ data.value || 'N/A' }}
        </template>

        <template #cell(verified)="data">
          <b-badge :variant="data.value ? 'success' : 'warning'">
            {{ data.value ? 'Verified' : 'Unverified' }}
          </b-badge>
        </template>

        <template #cell(status)="data">
          <b-badge :variant="getStatusVariant(data.item)">
            {{ data.item.is_suspended ? 'Suspended' : 'Active' }}
          </b-badge>
        </template>

        <template #cell(created_at)="data">
          {{ formatDate(data.value) }}
        </template>

        <template #cell(actions)="data">
          <b-dropdown variant="link" no-caret right size="sm">
            <template #button-content>
              <feather type="more-vertical" class="feather-sm"></feather>
            </template>
            <b-dropdown-item @click="viewUser(data.item)">
              <feather type="eye" class="feather-sm mr-2"></feather>
              View Details
            </b-dropdown-item>
            <b-dropdown-item @click="openEditUser(data.item)">
              <feather type="edit-2" class="feather-sm mr-2"></feather>
              Edit User
            </b-dropdown-item>
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item
              v-if="!data.item.verified"
              @click="verifyUser(data.item)"
            >
              <feather type="check-circle" class="feather-sm mr-2"></feather>
              Verify User
            </b-dropdown-item>
            <b-dropdown-item
              v-if="!data.item.is_suspended"
              @click="suspendUser(data.item)"
              variant="danger"
            >
              <feather type="user-x" class="feather-sm mr-2"></feather>
              Suspend User
            </b-dropdown-item>
            <b-dropdown-item
              v-if="data.item.is_suspended"
              @click="activateUser(data.item)"
              variant="success"
            >
              <feather type="user-check" class="feather-sm mr-2"></feather>
              Activate User
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>

      <!-- Pagination -->
      <b-row>
        <b-col md="6">
          <span class="text-muted">
            Showing {{ (currentPage - 1) * perPage + 1 }} to
            {{ Math.min(currentPage * perPage, filteredUsers.length) }} of
            {{ filteredUsers.length }} users
          </span>
        </b-col>
        <b-col md="6">
          <b-pagination
            v-model="currentPage"
            :total-rows="filteredUsers.length"
            :per-page="perPage"
            align="right"
            size="sm"
          ></b-pagination>
        </b-col>
      </b-row>
    </b-card>

    <!-- User Detail Modal -->
    <b-modal
      id="userDetailModal"
      v-model="showUserModal"
      size="lg"
      title="User Details"
      hide-footer
    >
      <div v-if="selectedUser">
        <b-row>
          <b-col md="4" class="text-center mb-3">
            <b-avatar
              :src="selectedUser.profile?.profile_img"
              :text="displayName(selectedUser)[0]"
              size="120px"
              variant="primary"
            ></b-avatar>
          </b-col>
          <b-col md="8">
            <h4>{{ displayName(selectedUser) }}</h4>
            <p class="text-muted">{{ selectedUser.email }}</p>
            <b-badge :variant="selectedUser.user_type === 1 ? 'success' : 'info'">
              {{ selectedUser.user_type === 1 ? 'Teacher' : 'Student' }}
            </b-badge>
            <b-badge :variant="selectedUser.verified ? 'success' : 'warning'" class="ml-2">
              {{ selectedUser.verified ? 'Verified' : 'Unverified' }}
            </b-badge>
            <b-badge v-if="selectedUser.is_suspended" variant="danger" class="ml-2">
              Suspended
            </b-badge>
            <b-badge v-if="selectedUser.approval_status" variant="secondary" class="ml-2">
              {{ selectedUser.approval_status }}
            </b-badge>
          </b-col>
        </b-row>

        <hr />

        <b-row>
          <b-col md="6">
            <strong>User ID:</strong> {{ selectedUser.user_id }}
          </b-col>
          <b-col md="6">
            <strong>Phone:</strong> {{ selectedUser.phone || 'N/A' }}
          </b-col>
          <b-col md="6" class="mt-2">
            <strong>Location:</strong> {{ selectedUser.profile?.location || 'N/A' }}
          </b-col>
        </b-row>

        <b-row class="mt-3" v-if="selectedUser.user_type === 1">
          <b-col md="12">
            <strong>Education:</strong> {{ selectedUser.profile?.education || 'N/A' }}
          </b-col>
          <b-col md="12" class="mt-2">
            <strong>Bio:</strong> {{ selectedUser.profile?.bio || 'N/A' }}
          </b-col>
          <b-col md="12" class="mt-2">
            <strong>Subjects:</strong>
            <b-badge
              v-for="subject in selectedUser.profile?.subjects"
              :key="subject.id"
              variant="primary"
              class="mr-1"
            >
              {{ subject.title }}
            </b-badge>
          </b-col>
          <b-col md="6" class="mt-2">
            <strong>Teaching Level:</strong> {{ selectedUser.profile?.teaching_level ?? 'N/A' }}
          </b-col>
          <b-col md="6" class="mt-2" v-if="selectedUser.profile?.weekdays">
            <strong>Weekdays:</strong>
            {{ selectedUser.profile.weekdays.map(d => d.title || d).join(', ') }}
          </b-col>
          <b-col md="12" class="mt-2" v-if="selectedUser.profile?.demo_url">
            <strong>Demo Video:</strong>
            <a :href="selectedUser.profile.demo_url" target="_blank">{{ selectedUser.profile.demo_url }}</a>
          </b-col>
        </b-row>

        <b-row class="mt-3" v-if="selectedUser.user_type === 0">
          <b-col md="12">
            <strong>Study Level:</strong> {{ selectedUser.profile?.study_level ?? 'N/A' }}
          </b-col>
        </b-row>

        <hr v-if="selectedUser.is_suspended" />

        <b-row v-if="selectedUser.is_suspended">
          <b-col md="12">
            <strong>Suspension Reason:</strong> {{ selectedUser.suspension_reason || 'N/A' }}
          </b-col>
          <b-col md="12" class="mt-2">
            <strong>Suspended On:</strong> {{ formatDate(selectedUser.suspended_at) }}
          </b-col>
        </b-row>

        <hr />

        <b-row>
          <b-col md="6">
            <strong>Joined:</strong> {{ formatDate(selectedUser.created_at) }}
          </b-col>
        </b-row>

        <div class="mt-4 text-right">
          <b-button variant="outline-primary" @click="openEditUser(selectedUser)" class="mr-2">
            <feather type="edit-2" class="feather-sm mr-2"></feather>
            Edit User
          </b-button>
          <b-button variant="secondary" @click="showUserModal = false">
            Close
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- Edit User Modal -->
    <b-modal
      v-model="showEditModal"
      size="lg"
      title="Edit User"
      scrollable
      @ok.prevent="saveUserEdits"
      :ok-disabled="savingUser"
      :ok-title="savingUser ? 'Saving...' : 'Save Changes'"
      ok-variant="primary"
      cancel-title="Cancel"
    >
      <div v-if="editForm">
        <h5 class="mb-3">Basic Information</h5>
        <b-row>
          <b-col md="6">
            <b-form-group label="First Name">
              <b-form-input v-model="editForm.firstName"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Last Name">
              <b-form-input v-model="editForm.lastName"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Email">
              <b-form-input type="email" v-model="editForm.email"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Phone">
              <b-form-input v-model="editForm.phone"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group label="Country">
              <b-form-select v-model="editForm.country" :options="countryOptions" @change="editForm.city = ''"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="City">
              <b-form-select v-model="editForm.city" :options="cityOptions" :disabled="!editForm.country"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="12" v-if="!editForm.country && editForm.location">
            <small class="text-muted">Current location (free text): {{ editForm.location }} — pick a country to replace it.</small>
          </b-col>
        </b-row>

        <template v-if="editForm.userType === 0">
          <hr />
          <h5 class="mb-3">Student Details</h5>
          <b-row>
            <b-col md="6">
              <b-form-group label="Study Level">
                <b-form-select v-model="editForm.studyLevel" :options="levelOptions"></b-form-select>
              </b-form-group>
            </b-col>
          </b-row>
        </template>

        <template v-if="editForm.userType === 1">
          <hr />
          <h5 class="mb-3">Teacher Details</h5>
          <b-row>
            <b-col md="12">
              <b-form-group label="Education">
                <b-form-input v-model="editForm.education"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group label="Bio">
                <b-form-textarea v-model="editForm.bio" rows="3"></b-form-textarea>
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group label="Demo Video URL (YouTube)">
                <b-form-input v-model="editForm.demoUrl"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Teaching Level">
                <b-form-select v-model="editForm.teachingLevel" :options="levelOptions"></b-form-select>
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group label="Subjects">
                <b-form-checkbox-group v-model="editForm.subjectIds" :options="subjectOptions"></b-form-checkbox-group>
              </b-form-group>
            </b-col>
            <b-col md="12">
              <b-form-group label="Weekdays">
                <b-form-checkbox-group v-model="editForm.weekdayIds" :options="weekdayOptions"></b-form-checkbox-group>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <label class="mb-1">Class Start Time</label>
              <b-row>
                <b-col cols="6">
                  <b-form-select v-model.number="editForm.startHour" :options="hourOptions"></b-form-select>
                </b-col>
                <b-col cols="6">
                  <b-form-select v-model.number="editForm.startMinute" :options="minuteOptions"></b-form-select>
                </b-col>
              </b-row>
            </b-col>
            <b-col md="6">
              <label class="mb-1">Class End Time</label>
              <b-row>
                <b-col cols="6">
                  <b-form-select v-model.number="editForm.endHour" :options="hourOptions"></b-form-select>
                </b-col>
                <b-col cols="6">
                  <b-form-select v-model.number="editForm.endMinute" :options="minuteOptions"></b-form-select>
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <hr />
          <h5 class="mb-3">Bank Details (Payout)</h5>
          <b-row>
            <b-col md="4">
              <b-form-group label="Bank Name">
                <b-form-input v-model="editForm.bankName"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Account Number">
                <b-form-input v-model="editForm.accountNumber"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="IBAN">
                <b-form-input v-model="editForm.iban"></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
        </template>
      </div>
    </b-modal>
  </div>
</template>

<script>
import UserService from '@/services/user.service';
import CategoryService from '@/services/category.service';
import moment from 'moment';
import { LOCATIONS, OTHER_CITY, composeLocation } from '@/constants/locations';

// Matches the weekday id scheme used by the mobile app's schedule picker
// (0 = Monday ... 6 = Sunday).
const WEEKDAYS = [
  { id: 0, title: 'Monday' },
  { id: 1, title: 'Tuesday' },
  { id: 2, title: 'Wednesday' },
  { id: 3, title: 'Thursday' },
  { id: 4, title: 'Friday' },
  { id: 5, title: 'Saturday' },
  { id: 6, title: 'Sunday' },
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => ({ value: h, text: h.toString().padStart(2, '0') }));
const MINUTE_OPTIONS = [0, 15, 30, 45].map(m => ({ value: m, text: m.toString().padStart(2, '0') }));

export default {
  name: 'UserManagement',
  data() {
    return {
      users: [],
      filteredUsers: [],
      loading: false,
      filter: {
        userType: null,
        status: null,
        search: '',
      },
      stats: {
        totalUsers: 0,
        students: 0,
        teachers: 0,
        pendingVerification: 0,
      },
      fields: [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'user_type', label: 'Type', sortable: true },
        { key: 'phone', label: 'Phone' },
        { key: 'verified', label: 'Verified', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'created_at', label: 'Joined', sortable: true },
        { key: 'actions', label: 'Actions' },
      ],
      perPage: 10,
      currentPage: 1,
      showUserModal: false,
      selectedUser: null,
      categories: [],
      levels: [],
      showEditModal: false,
      savingUser: false,
      editForm: null,
    };
  },
  created() {
    this.loadUsers();
    this.loadCategories();
    this.loadLevels();
  },
  computed: {
    countryOptions() {
      return [{ value: '', text: '— Select country —' }, ...LOCATIONS.map(l => ({ value: l.country, text: l.country }))];
    },
    cityOptions() {
      const cities = (LOCATIONS.find(l => l.country === this.editForm?.country)?.cities) || [];
      return [{ value: '', text: '— Select city —' }, ...[...cities, OTHER_CITY].map(c => ({ value: c, text: c }))];
    },
    levelOptions() {
      const options = this.levels.map(l => ({ value: l.level_id, text: l.level_title }));
      return [{ value: null, text: 'Select a level...' }, ...options];
    },
    subjectOptions() {
      return this.categories.map(c => ({ value: c.category_id, text: c.category_title }));
    },
    weekdayOptions() {
      return WEEKDAYS.map(w => ({ value: w.id, text: w.title }));
    },
    hourOptions() {
      return HOUR_OPTIONS;
    },
    minuteOptions() {
      return MINUTE_OPTIONS;
    },
  },
  methods: {
    async loadUsers() {
      this.loading = true;
      try {
        const response = await UserService.getAllUsers();
        this.users = response.data;
        this.filteredUsers = this.users;
        this.calculateStats();
      } catch (error) {
        this.$bvToast.toast('Failed to load users', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.loading = false;
      }
    },
    calculateStats() {
      this.stats.totalUsers = this.users.length;
      this.stats.students = this.users.filter(u => u.user_type === 0).length;
      this.stats.teachers = this.users.filter(u => u.user_type === 1).length;
      this.stats.pendingVerification = this.users.filter(u => !u.verified).length;
    },
    filterUsers() {
      let result = [...this.users];

      if (this.filter.userType !== null) {
        result = result.filter(u => u.user_type === this.filter.userType);
      }

      if (this.filter.status) {
        switch (this.filter.status) {
          case 'active':
            result = result.filter(u => !u.is_suspended);
            break;
          case 'suspended':
            result = result.filter(u => u.is_suspended);
            break;
          case 'verified':
            result = result.filter(u => u.verified);
            break;
          case 'unverified':
            result = result.filter(u => !u.verified);
            break;
        }
      }

      if (this.filter.search) {
        const search = this.filter.search.toLowerCase();
        result = result.filter(
          u =>
            this.displayName(u).toLowerCase().includes(search) ||
            u.email?.toLowerCase().includes(search) ||
            u.phone?.includes(search)
        );
      }

      this.filteredUsers = result;
      this.currentPage = 1;
    },
    searchUsers() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.filterUsers();
      }, 300);
    },
    refreshUsers() {
      this.loadUsers();
    },
    viewUser(user) {
      this.selectedUser = user;
      this.showUserModal = true;
    },
    displayName(user) {
      const profile = user?.profile || {};
      // fname/lname on the users row are legacy/unused (real names live in
      // profile.first_name/last_name); fall back to them only if profile is empty.
      const name = `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
        || `${user?.fname || ''} ${user?.lname || ''}`.trim();
      return name || user?.email || 'User';
    },
    async loadCategories() {
      try {
        const response = await CategoryService.getAllCategories();
        this.categories = response.data || [];
      } catch (error) {
        this.categories = [];
      }
    },
    async loadLevels() {
      try {
        const response = await CategoryService.getAllLevels();
        this.levels = response.data || [];
      } catch (error) {
        this.levels = [];
      }
    },
    openEditUser(user) {
      const profile = user.profile || {};
      const timings = Array.isArray(profile.timings) ? profile.timings : [900, 1700];

      // Keep the currently-assigned level/subjects selectable even if the
      // active-only lists don't include them (e.g. deactivated since).
      if (profile.study_level != null && !this.levels.some(l => l.level_id === profile.study_level)) {
        this.levels = [...this.levels, { level_id: profile.study_level, level_title: `#${profile.study_level} (inactive)` }];
      }
      if (profile.teaching_level != null && !this.levels.some(l => l.level_id === profile.teaching_level)) {
        this.levels = [...this.levels, { level_id: profile.teaching_level, level_title: `#${profile.teaching_level} (inactive)` }];
      }
      (profile.subjects || []).forEach(s => {
        if (!this.categories.some(c => c.category_id === s.id)) {
          this.categories = [...this.categories, { category_id: s.id, category_title: s.title || `#${s.id} (inactive)` }];
        }
      });

      this.editForm = {
        userId: user.user_id,
        userType: user.user_type,
        email: user.email || '',
        phone: user.phone || '',
        firstName: profile.first_name || '',
        lastName: profile.last_name || '',
        location: typeof profile.location === 'string' ? profile.location : '',
        country: profile.country || '',
        city: profile.city || '',
        studyLevel: profile.study_level ?? null,
        education: profile.education || '',
        bio: profile.bio || '',
        demoUrl: profile.demo_url || '',
        teachingLevel: profile.teaching_level ?? null,
        subjectIds: (profile.subjects || []).map(s => s.id),
        weekdayIds: (profile.weekdays || []).map(w => w.id),
        startHour: Math.floor((timings[0] || 0) / 100),
        startMinute: (timings[0] || 0) % 100,
        endHour: Math.floor((timings[1] || 0) / 100),
        endMinute: (timings[1] || 0) % 100,
        bankName: profile.bank_name || '',
        accountNumber: profile.account_number || '',
        iban: profile.iban || '',
        originalProfile: profile,
      };
      this.showEditModal = true;
    },
    async saveUserEdits() {
      if (!this.editForm) return;
      this.savingUser = true;
      try {
        let profile;
        if (this.editForm.userType === 1) {
          profile = {
            ...this.editForm.originalProfile,
            first_name: this.editForm.firstName,
            last_name: this.editForm.lastName,
            location: composeLocation(this.editForm.country, this.editForm.city) || this.editForm.location,
            country: this.editForm.country,
            city: this.editForm.city,
            education: this.editForm.education,
            bio: this.editForm.bio,
            demo_url: this.editForm.demoUrl,
            teaching_level: this.editForm.teachingLevel,
            subjects: this.categories
              .filter(c => this.editForm.subjectIds.includes(c.category_id))
              .map(c => ({ id: c.category_id, title: c.category_title })),
            weekdays: WEEKDAYS.filter(w => this.editForm.weekdayIds.includes(w.id)),
            timings: [
              Number(this.editForm.startHour) * 100 + Number(this.editForm.startMinute),
              Number(this.editForm.endHour) * 100 + Number(this.editForm.endMinute),
            ],
            bank_name: this.editForm.bankName,
            account_number: this.editForm.accountNumber,
            iban: this.editForm.iban,
          };
        } else {
          profile = {
            ...this.editForm.originalProfile,
            first_name: this.editForm.firstName,
            last_name: this.editForm.lastName,
            location: composeLocation(this.editForm.country, this.editForm.city) || this.editForm.location,
            country: this.editForm.country,
            city: this.editForm.city,
            study_level: this.editForm.studyLevel,
          };
        }

        await UserService.adminUpdateUser(this.editForm.userId, {
          email: this.editForm.email,
          phone: this.editForm.phone,
          profile,
        });
        this.$bvToast.toast('User updated successfully', {
          title: 'Success',
          variant: 'success',
          solid: true,
        });
        this.showEditModal = false;
        if (this.selectedUser && this.selectedUser.user_id === this.editForm.userId) {
          this.showUserModal = false;
        }
        this.loadUsers();
      } catch (error) {
        this.$bvToast.toast(error.response?.data?.message || 'Failed to update user', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.savingUser = false;
      }
    },
    async verifyUser(user) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to verify ${this.displayName(user)}?`,
        {
          title: 'Confirm Verification',
          okVariant: 'success',
          okTitle: 'Verify',
          cancelTitle: 'Cancel',
        }
      );

      if (confirmed) {
        try {
          await UserService.verifyUser(user.user_id, { verified: true });
          this.$bvToast.toast('User verified successfully', {
            title: 'Success',
            variant: 'success',
            solid: true,
          });
          this.loadUsers();
        } catch (error) {
          this.$bvToast.toast('Failed to verify user', {
            title: 'Error',
            variant: 'danger',
            solid: true,
          });
        }
      }
    },
    async suspendUser(user) {
      const reason = await this.$bvModal.msgBoxConfirm(
        `Please provide a reason for suspending ${this.displayName(user)}`,
        {
          title: 'Suspend User',
          okVariant: 'danger',
          okTitle: 'Suspend',
          cancelTitle: 'Cancel',
        }
      );

      if (reason) {
        try {
          await UserService.suspendUser(user.user_id, reason);
          this.$bvToast.toast('User suspended successfully', {
            title: 'Success',
            variant: 'success',
            solid: true,
          });
          this.loadUsers();
        } catch (error) {
          this.$bvToast.toast('Failed to suspend user', {
            title: 'Error',
            variant: 'danger',
            solid: true,
          });
        }
      }
    },
    async activateUser(user) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to activate ${this.displayName(user)}?`,
        {
          title: 'Confirm Activation',
          okVariant: 'success',
          okTitle: 'Activate',
          cancelTitle: 'Cancel',
        }
      );

      if (confirmed) {
        try {
          await UserService.activateUser(user.user_id);
          this.$bvToast.toast('User activated successfully', {
            title: 'Success',
            variant: 'success',
            solid: true,
          });
          this.loadUsers();
        } catch (error) {
          this.$bvToast.toast('Failed to activate user', {
            title: 'Error',
            variant: 'danger',
            solid: true,
          });
        }
      }
    },
    getStatusVariant(user) {
      if (user.is_suspended) return 'danger';
      return 'success';
    },
    formatDate(date) {
      return moment(date).format('MMM DD, YYYY');
    },
  },
};
</script>

<style scoped>
.feather-sm {
  width: 16px;
  height: 16px;
}
</style>
